import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { db } from '../firebase/firebaseConfig';
import { collection, doc, getDocs, query, where, onSnapshot } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import '../styling/dataView.css';

const DataView = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentView, setCurrentView] = useState('');
  const [userPractice, setUserPractice] = useState('');
  const [userRole, setUserRole] = useState('');
  const [buttonCounts, setButtonCounts] = useState({});

  const clinicalButtons = useMemo(() => ([
    { label: 'Success - Anesthesia', collection: 'Success', subCollection: 'Anesthesia', style: 'green', icon: '✅' },
    { label: 'Emergency', collection: 'Emergency', subCollection: '', style: 'red', icon: '🚑' },
    { label: 'Complication - Anesthesia', collection: 'Complications', subCollection: 'Anesthesia', style: 'orange', icon: '🩺' },
    { label: 'Complication - Dental Implant', collection: 'Complications', subCollection: 'Dental Implant', style: 'orange', icon: '🦷' },
    { label: 'Complication - Dentoalveolar Surgery', collection: 'Complications', subCollection: 'Dentoalveolar Surgery', style: 'orange', icon: '🔪' },
    { label: 'Complication - Needle Stick Injury', collection: 'Complications', subCollection: 'Needle Stick Injury', style: 'orange', icon: '💉' },
  ]), []);

  const safetyButtons = useMemo(() => ([
    { label: 'Aggressive Patient Interaction', collection: 'AggressivePatientInteractions', subCollection: 'Entries', style: 'safety', icon: '🛡️' },
  ]), []);

  const operationsButtons = useMemo(() => ([
    // Equipment (shared icon)
    { label: 'Equipment - Surgical', collection: 'Operations', subCollection: 'Equipment___Surgical', style: 'ops', icon: '🔧' },
    { label: 'Equipment - Imaging', collection: 'Operations', subCollection: 'Equipment___Imaging', style: 'ops', icon: '🔧' },
    { label: 'Equipment - Sterilization', collection: 'Operations', subCollection: 'Equipment___Sterilization', style: 'ops', icon: '🔧' },
    { label: 'Equipment - Anesthesia', collection: 'Operations', subCollection: 'Equipment___Anesthesia', style: 'ops', icon: '🔧' },
    { label: 'Equipment - Computer/Software', collection: 'Operations', subCollection: 'Equipment___Computer_Software', style: 'ops', icon: '🔧' },
    { label: 'Equipment - Facility Systems', collection: 'Operations', subCollection: 'Equipment___Facility_Systems', style: 'ops', icon: '🔧' },
    // Complaints (using speech bubble emoji)
    { label: 'Complaint - Clinical Care', collection: 'Operations', subCollection: 'Complaint___Clinical_Care', style: 'ops', icon: '💬' },
    { label: 'Complaint - Customer Service', collection: 'Operations', subCollection: 'Complaint___Customer_Service', style: 'ops', icon: '💬' },
    { label: 'Complaint - Billing/Financial', collection: 'Operations', subCollection: 'Complaint___Billing_Financial', style: 'ops', icon: '💬' },
    { label: 'Complaint - Facility/Environment', collection: 'Operations', subCollection: 'Complaint___Facility_Environment', style: 'ops', icon: '💬' },
    // Scheduling (using calendar emoji)
    { label: 'Scheduling - Appointment', collection: 'Operations', subCollection: 'Scheduling___Appointment', style: 'ops', icon: '📅' },
    { label: 'Scheduling - Communication', collection: 'Operations', subCollection: 'Scheduling___Communication', style: 'ops', icon: '📅' },
    { label: 'Scheduling - Documentation', collection: 'Operations', subCollection: 'Scheduling___Documentation', style: 'ops', icon: '📅' },
    { label: 'Scheduling - System/Process', collection: 'Operations', subCollection: 'Scheduling___System_Process', style: 'ops', icon: '📅' },
  ]), []);

  const pillButtons = useMemo(() => ([
    ...clinicalButtons
  ]), [clinicalButtons]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (user) {
        const userDoc = await getDocs(query(collection(db, 'users'), where('email', '==', user.email.toLowerCase())));
        if (!userDoc.empty) {
          const userData = userDoc.docs[0].data();
          setUserPractice(userData.practice || '');
          setUserRole(userData.role || '');
        }
      }
    };
    fetchUserInfo();
  }, []);

  const getCount = useCallback((collectionName, subCollectionName = '', label) => {
    let unsubscribe;
    try {
      // For System Administrators, don't filter by practice (show all data)
      // For other roles, filter by their assigned practice
      const shouldFilterByPractice = userRole !== 'System Administrator' && userPractice;
      
      if (collectionName === 'Success') {
        const successRefDoc = doc(db, 'Success', 'successRefDoc');
        const subCollectionRef = collection(successRefDoc, subCollectionName);
        const q = shouldFilterByPractice 
          ? query(subCollectionRef, where("practice", "==", userPractice))
          : subCollectionRef;
        unsubscribe = onSnapshot(q, (querySnapshot) => {
          setButtonCounts(prevCounts => ({
            ...prevCounts,
            [label]: querySnapshot.size
          }));
        });
      } else if (collectionName === 'Complications') {
        const formRefDoc = doc(db, 'Complications', 'formRefDoc');
        const subCollectionRef = collection(formRefDoc, subCollectionName);
        const q = shouldFilterByPractice 
          ? query(subCollectionRef, where("practice", "==", userPractice))
          : subCollectionRef;
        unsubscribe = onSnapshot(q, (querySnapshot) => {
          setButtonCounts(prevCounts => ({
            ...prevCounts,
            [label]: querySnapshot.size
          }));
        });
      } else if (collectionName === 'Operations') {
        const parentRef = doc(db, 'Operations', 'opsRefDoc');
        const subRef = collection(parentRef, subCollectionName);
        const q = shouldFilterByPractice 
          ? query(subRef, where("practice", "==", userPractice))
          : subRef;
        unsubscribe = onSnapshot(q, (querySnapshot) => {
          setButtonCounts(prevCounts => ({
            ...prevCounts,
            [label]: querySnapshot.size
          }));
        });
      } else if (collectionName === 'AggressivePatientInteractions') {
        const parentRef = doc(db, 'AggressivePatientInteractions', 'root');
        const subRef = collection(parentRef, subCollectionName);
        const q = shouldFilterByPractice 
          ? query(subRef, where("practice", "==", userPractice))
          : subRef;
        unsubscribe = onSnapshot(q, (querySnapshot) => {
          setButtonCounts(prevCounts => ({
            ...prevCounts,
            [label]: querySnapshot.size
          }));
        });
      } else {
        const collectionRef = collection(db, collectionName);
        const q = shouldFilterByPractice 
          ? query(collectionRef, where("practice", "==", userPractice))
          : collectionRef;
        unsubscribe = onSnapshot(q, (querySnapshot) => {
          setButtonCounts(prevCounts => ({
            ...prevCounts,
            [label]: querySnapshot.size
          }));
        });
      }
      return unsubscribe;
    } catch (error) {
      console.error("Error fetching count:", error);
      return null;
    }
  }, [userPractice, userRole]);

  const fetchAllCounts = useCallback(() => {
    const unsubscribes = [];
    const allButtons = [...clinicalButtons, ...safetyButtons, ...operationsButtons];
    for (const b of allButtons) {
      const { collection: collectionName, subCollection: subCollectionName, label } = b;
      unsubscribes.push(getCount(collectionName, subCollectionName, label));
    }
    return () => {
      unsubscribes.forEach(unsub => unsub && unsub());
    };
  }, [clinicalButtons, safetyButtons, operationsButtons, getCount]);
  
  useEffect(() => {
    let unsubscribe;
    if (userRole === 'System Administrator' || userPractice) {
      unsubscribe = fetchAllCounts();
    }
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [userPractice, userRole, fetchAllCounts]);
  

  const fetchData = useCallback((collectionName, subCollectionName = '') => {
    if (userRole !== 'System Administrator' && !userPractice) {
      console.error("No user practice found for non-System Administrator user");
      return;
    }
  
    setLoading(true);
    setCurrentView(subCollectionName ? `${collectionName} - ${subCollectionName}` : collectionName);
  
    try {
      let unsubscribe;
      const shouldFilterByPractice = userRole !== 'System Administrator' && userPractice;
      
      if (collectionName === 'Success') {
        const successRefDoc = doc(db, 'Success', 'successRefDoc');
        const subCollectionRef = collection(successRefDoc, subCollectionName);
        const q = shouldFilterByPractice 
          ? query(subCollectionRef, where("practice", "==", userPractice))
          : subCollectionRef;
        unsubscribe = onSnapshot(q, (querySnapshot) => {
          const fetchedData = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setData(fetchedData);
          setLoading(false);
        });
      } else if (collectionName === 'Complications') {
        const formRefDoc = doc(db, 'Complications', 'formRefDoc');
        const subCollectionRef = collection(formRefDoc, subCollectionName);
        const q = shouldFilterByPractice 
          ? query(subCollectionRef, where("practice", "==", userPractice))
          : subCollectionRef;
        unsubscribe = onSnapshot(q, (querySnapshot) => {
          const fetchedData = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setData(fetchedData);
          setLoading(false);
        });
      } else if (collectionName === 'Operations') {
        const parentRef = doc(db, 'Operations', 'opsRefDoc');
        const subRef = collection(parentRef, subCollectionName);
        const q = shouldFilterByPractice 
          ? query(subRef, where("practice", "==", userPractice))
          : subRef;
        unsubscribe = onSnapshot(q, (querySnapshot) => {
          const fetchedData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setData(fetchedData);
          setLoading(false);
        });
      } else if (collectionName === 'AggressivePatientInteractions') {
        const parentRef = doc(db, 'AggressivePatientInteractions', 'root');
        const subRef = collection(parentRef, subCollectionName);
        const q = shouldFilterByPractice 
          ? query(subRef, where("practice", "==", userPractice))
          : subRef;
        unsubscribe = onSnapshot(q, (querySnapshot) => {
          const fetchedData = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setData(fetchedData);
          setLoading(false);
        });
      } else {
        const collectionRef = collection(db, collectionName);
        const q = shouldFilterByPractice 
          ? query(collectionRef, where("practice", "==", userPractice))
          : collectionRef;
        unsubscribe = onSnapshot(q, (querySnapshot) => {
          const fetchedData = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setData(fetchedData);
          setLoading(false);
        });
      }
  
      return () => unsubscribe();
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  }, [userPractice, userRole]);
  

  const handleView = useCallback((collectionName, subCollectionName = '') => {
    fetchData(collectionName, subCollectionName);
  }, [fetchData]);

  const renderDataItem = useCallback((item) => (
    <div key={item.id} className="data-item">
      <h4>Document ID: {item.id}</h4>
      {Object.entries(item).map(([key, value]) => (
        key !== 'id' && (
          <p key={key}>
            <strong>{key}:</strong> {typeof value === 'object' ? JSON.stringify(value) : value}
          </p>
        )
      ))}
      <hr />
    </div>
  ), []);

  const renderDataSection = useCallback(() => (
    <div className="data-section">
      {data.length === 0 ? (
        <p>No data available</p>
      ) : (
        data.map(renderDataItem)
      )}
    </div>
  ), [data, renderDataItem]);

  if (userRole !== 'System Administrator' && !userPractice) {
    return <div>Loading user data...</div>;
  }

  return (
    <div className="data-viewer">
      <h1>Procedure Completion Dashboard</h1>
      <h2>{userRole === 'System Administrator' ? 'All Practices' : userPractice}</h2>

      <div className="section-card">
        <div className="section-header">
          <span className="section-icon">📊</span>
          <span className="section-title">Clinical Outcomes</span>
        </div>
        <div className="cards-grid">
          {pillButtons.map((b, i) => (
            <div key={`c-${i}`} className={`outcome-card outcome-${b.style}`} onClick={() => handleView(b.collection, b.subCollection)}>
              <div className="card-image">{b.icon}</div>
              <div className="card-label">{b.label}</div>
              <div className="card-count">{buttonCounts[b.label] || 0}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="section-card">
        <div className="section-header">
          <span className="section-icon">🛡️</span>
          <span className="section-title">Safety & Security</span>
        </div>
        <div className="cards-grid">
          {safetyButtons.map((b, i) => (
            <div key={`s-${i}`} className={`outcome-card outcome-${b.style}`} onClick={() => handleView(b.collection, b.subCollection)}>
              <div className="card-image">{b.icon}</div>
              <div className="card-label">{b.label}</div>
              <div className="card-count">{buttonCounts[b.label] || 0}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="section-card">
        <div className="section-header">
          <span className="section-icon">⚙️</span>
          <span className="section-title">Operations & Quality</span>
        </div>
        <div className="cards-grid">
          {operationsButtons.map((b, i) => (
            <div key={`o-${i}`} className={`outcome-card outcome-${b.style}`} onClick={() => handleView(b.collection, b.subCollection)}>
              <div className="card-image">{b.icon}</div>
              <div className="card-label">{b.label}</div>
              <div className="card-count">{buttonCounts[b.label] || 0}</div>
            </div>
          ))}
        </div>
      </div>

      {currentView && (
        <div className="data-section">
          <h3>{currentView} Details</h3>
          {loading ? <div className="loading">Loading...</div> : renderDataSection()}
        </div>
      )}
    </div>
  );
};

export default DataView;
